'use client';

import { createContext, createElement, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { getFirestoreClient } from '@/lib/firebase/client';
import { getHomepageSettings } from '@/lib/platform/homepage';
import type { HomepageSettingsDoc } from '@/types/platform';

type HomepageSettingsContextValue = {
  settings: HomepageSettingsDoc | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const HomepageSettingsContext = createContext<HomepageSettingsContextValue | null>(null);

export function HomepageSettingsProvider({
  children,
  initialSettings,
}: {
  children: ReactNode;
  initialSettings: HomepageSettingsDoc | null;
}) {
  const [settings, setSettings] = useState<HomepageSettingsDoc | null>(initialSettings);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const db = getFirestoreClient();
    if (!db) {
      setSettings(null);
      setLoading(false);
      setError('Firebase client environment is not configured yet.');
      return;
    }
    try {
      setLoading(true);
      const data = await getHomepageSettings(db);
      setSettings(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load homepage settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo(() => ({ settings, loading, error, refresh }), [settings, loading, error, refresh]);

  return createElement(HomepageSettingsContext.Provider, { value }, children);
}

export function useHomepageSettings() {
  const context = useContext(HomepageSettingsContext);
  if (!context) {
    throw new Error('useHomepageSettings must be used within HomepageSettingsProvider');
  }
  return context;
}
